/**
 * Which of the run header's timing fields are worth showing.
 *
 * The header used to print all four unconditionally, which made most of the row
 * noise: a run that starts the moment it is created reports the same timestamp
 * twice, and a run still going shows `Finished: —` — a labelled placeholder for
 * a value that cannot exist yet. A field now appears only when it says
 * something the others don't.
 */

/**
 * Queue waits shorter than this are a rounding artifact, not a wait: Created and
 * Started are the same moment to anyone reading them.
 */
const QUEUE_WAIT_VISIBLE_MS = 1000;

export type RunTimingVisibility = {
  showCreated: boolean;
  showStarted: boolean;
  showFinished: boolean;
  showDuration: boolean;
  /**
   * The "4d ago" in the title row. While a run is still going it measures the
   * same interval as the elapsed counter — both are `now - startedAt` — so only
   * one of the two is shown.
   */
  showRelativeAge: boolean;
  /** A run still going has no final duration — it has elapsed time. */
  durationLabel: 'Duration' | 'Running for';
};

export function runTimingVisibility(run: {
  createdAt: Date;
  startedAt: Date | null;
  finishedAt: Date | null;
}): RunTimingVisibility {
  const { createdAt, startedAt, finishedAt } = run;
  const isRunning = startedAt !== null && finishedAt === null;
  const queueWaitMs =
    startedAt === null ? 0 : startedAt.getTime() - createdAt.getTime();

  return {
    // Created earns a slot only when the run waited to start; otherwise Started
    // already reports that moment. A run that has not started yet has nothing
    // else to show, so it keeps Created.
    showCreated: startedAt === null || queueWaitMs >= QUEUE_WAIT_VISIBLE_MS,
    showStarted: startedAt !== null,
    showFinished: finishedAt !== null,
    showDuration: startedAt !== null,
    // The elapsed counter wins the tie while running: it ticks, and it stays
    // precise where the relative age rounds a 12-second run to "just now". Once
    // the run ends the two say different things and both come back.
    showRelativeAge: !isRunning,
    durationLabel: finishedAt === null ? 'Running for' : 'Duration',
  };
}
